import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  getScheduleSettings,
  updateScheduleSettings,
  updateBoostSettings,
  listBoostChanges,
  runPublishDue,
  listReadyArticles,
  getPipelineSnapshot,
} from "@/lib/editorial.functions";
import { toast } from "sonner";
import { formatDate } from "@/lib/site";
import { useRealtimeInvalidate } from "@/hooks/useRealtimeInvalidate";

export const Route = createFileRoute("/admin/schedule")({
  component: SchedulePage,
});

function SchedulePage() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["admin", "schedule"],
    queryFn: () => getScheduleSettings(),
  });
  const { data: ready } = useQuery({
    queryKey: ["admin", "ready"],
    queryFn: () => listReadyArticles(),
    refetchInterval: 10000,
  });
  const { data: snapshot } = useQuery({
    queryKey: ["admin", "pipeline-snapshot"],
    queryFn: () => getPipelineSnapshot(),
    refetchInterval: 15000,
  });
  const { data: boostHistory } = useQuery({
    queryKey: ["admin", "boost-history"],
    queryFn: () => listBoostChanges(),
    refetchInterval: 30000,
  });
  useRealtimeInvalidate([{ table: "articles", queryKey: ["admin", "ready"] }]);


  const [form, setForm] = useState({
    articles_per_day: 2,
    publish_times: ["09:00", "15:00"],
    timezone: "Europe/London",
    auto_publish: false,
    min_quality_score: 70,
    tolerance_floor: 15,
  });

  // Boost lives in server-authoritative state; the UI mirrors DB values and
  // is written through its own mutation, so it can never be armed as a
  // side effect of saving the main schedule form.
  const boost = settings as
    | { boost_articles_per_day?: number | null; boost_until?: string | null }
    | undefined;
  const boostRate = boost?.boost_articles_per_day ?? null;
  const boostUntil = boost?.boost_until ?? null;
  const boostActive =
    boostRate != null &&
    boostUntil != null &&
    new Date(boostUntil).getTime() > Date.now();
  const effectiveRate = boostActive ? boostRate! : form.articles_per_day;
  const daysLeft = boostActive
    ? Math.max(
        0,
        Math.ceil((new Date(boostUntil!).getTime() - Date.now()) / 86_400_000),
      )
    : 0;

  // Seed the form from the server ONCE. Background refetches must not
  // clobber unsaved local edits — that was how boost fields silently
  // returned to old values between edit and save.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!settings || hydratedRef.current) return;
    const s = settings as any;
    setForm({
      articles_per_day: s.articles_per_day ?? 2,
      publish_times: (s.publish_times ?? ["09:00", "15:00"]).map((t: string) =>
        t.slice(0, 5),
      ),
      timezone: s.timezone ?? "Europe/London",
      auto_publish: s.auto_publish ?? false,
      min_quality_score: s.min_quality_score ?? 70,
      tolerance_floor: s.tolerance_floor ?? 15,
    });
    hydratedRef.current = true;
  }, [settings]);

  const save = useMutation({
    mutationFn: () => updateScheduleSettings({ data: form }),
    onSuccess: () => {
      toast.success("Schedule saved");
      hydratedRef.current = false; // allow fresh hydrate from server
      qc.invalidateQueries({ queryKey: ["admin", "schedule"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const boostMutation = useMutation({
    mutationFn: (input: {
      boost_articles_per_day: number | null;
      boost_until: string | null;
    }) => updateBoostSettings({ data: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "schedule"] });
      qc.invalidateQueries({ queryKey: ["admin", "boost-history"] });
      qc.invalidateQueries({ queryKey: ["admin", "pipeline-snapshot"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  function startBoost(rate: number, days: number) {
    if (
      !window.confirm(
        `Start temporary boost at ${rate}/day for ${days} days? This overrides the daily cap until it expires.`,
      )
    ) {
      return;
    }
    const until = new Date(Date.now() + days * 86_400_000).toISOString();
    boostMutation.mutate(
      { boost_articles_per_day: rate, boost_until: until },
      { onSuccess: () => toast.success(`Boost started: ${rate}/day for ${days}d`) },
    );
  }
  function cancelBoost() {
    if (!window.confirm("Cancel the active boost and return to the daily cap?")) return;
    boostMutation.mutate(
      { boost_articles_per_day: null, boost_until: null },
      { onSuccess: () => toast.success("Boost cancelled") },
    );
  }

  const publishNow = useMutation({
    mutationFn: () => runPublishDue(),
    onSuccess: (r: any) => {
      if (r.published === 0) toast.info("Nothing due to publish.");
      else toast.success(`Published ${r.published}: ${r.titles.join(", ")}`);
      qc.invalidateQueries({ queryKey: ["admin", "ready"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  function updateTime(i: number, val: string) {
    const next = [...form.publish_times];
    next[i] = val;
    setForm({ ...form, publish_times: next });
  }
  function addTime() {
    setForm({ ...form, publish_times: [...form.publish_times, "12:00"] });
  }
  function removeTime(i: number) {
    setForm({ ...form, publish_times: form.publish_times.filter((_, j) => j !== i) });
  }


  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
      <section>
        <h1 className="headline-display text-3xl">Publish schedule</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Auto-publish promotes the highest-quality ready articles up to the daily cap.
          The publisher cron runs every 15 minutes.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          The queue below shows <em>Ready</em> and <em>Scheduled</em> articles — a subset of the unpublished rows on the <Link to="/admin/articles" search={{ filter: "all" as const, sort: "published_at" as const, dir: "desc" as const, page: 1, pageSize: 25 as const }} className="underline">Articles</Link> tab. Drafts still with the editor don't appear here yet.
        </p>

        {snapshot && (
          <div className="mt-6 grid gap-2 border border-border bg-paper-soft p-4 text-sm sm:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Effective rate</div>
              <div className="mt-1 font-medium">
                {snapshot.effectiveRate}/day{snapshot.boostActive ? " · boost active" : ""}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Publishable supply</div>
              <div className="mt-1 font-medium">
                {snapshot.readyCount} ready · {snapshot.toleranceDraftCount} fallback drafts ≥{snapshot.toleranceMin}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Generation queue</div>
              <div className="mt-1 font-medium">
                {snapshot.pendingTaskCount} pending · {snapshot.runningTaskCount} running
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Latest publisher decision</div>
              <div className="mt-1 font-medium line-clamp-2">
                {snapshot.latestDecision?.reason ?? "No decision logged yet"}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 border border-border p-6 bg-paper-soft">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="font-medium">Boost (temporary catch-up)</h2>
            <span
              className={`text-xs uppercase tracking-wider ${
                boostActive ? "text-emerald-700" : "text-muted-foreground"
              }`}
            >
              {boostActive ? `Active · ${daysLeft}d left` : "Inactive"}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Publish faster for a fixed window to drain the backlog, then auto-revert to
            the daily cap below. Effective rate right now:{" "}
            <strong>{effectiveRate}/day</strong>.
            {boostActive && boostUntil && (
              <> Boost ends {new Date(boostUntil).toLocaleString()}.</>
            )}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Boost changes are saved <strong>immediately</strong> and are separate from
            the "Save settings" button below, so unrelated edits can't re-arm a boost.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => startBoost(10, 14)}
              disabled={boostMutation.isPending}
              className="border border-border px-3 py-1.5 text-sm"
            >
              10/day · 14 days
            </button>
            <button
              onClick={() => startBoost(6, 14)}
              disabled={boostMutation.isPending}
              className="border border-border px-3 py-1.5 text-sm"
            >
              6/day · 14 days
            </button>
            <button
              onClick={() => startBoost(10, 7)}
              disabled={boostMutation.isPending}
              className="border border-border px-3 py-1.5 text-sm"
            >
              10/day · 7 days
            </button>
            {boostActive && (
              <button
                onClick={cancelBoost}
                disabled={boostMutation.isPending}
                className="border border-border px-3 py-1.5 text-sm text-muted-foreground"
              >
                Cancel boost
              </button>
            )}
          </div>
          {boostHistory && boostHistory.length > 0 && (
            <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
              <div className="uppercase tracking-wider">Recent boost changes</div>
              <ul className="mt-2 space-y-1">
                {boostHistory.slice(0, 5).map((h: any) => {
                  const nv = h.new_value ?? {};
                  const armed = nv.boost_articles_per_day != null;
                  return (
                    <li key={h.id} className="flex justify-between gap-3">
                      <span>
                        {armed
                          ? `Armed ${nv.boost_articles_per_day}/day until ${
                              nv.boost_until ? new Date(nv.boost_until).toLocaleDateString() : "?"
                            }`
                          : "Cancelled"}
                      </span>
                      <span>{formatDate(h.changed_at)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>




        <div className="mt-8 space-y-5 border border-border p-6 bg-paper-soft">
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={form.auto_publish}
              onChange={(e) => setForm({ ...form, auto_publish: e.target.checked })}
            />
            Auto-publish when articles pass editor quality threshold
          </label>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Articles per day (cap)
            </label>
            <input
              type="number"
              min={0}
              max={10}
              value={form.articles_per_day}
              onChange={(e) =>
                setForm({ ...form, articles_per_day: Number(e.target.value) || 0 })
              }
              className="mt-1 w-24 border border-border bg-background px-3 py-2"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Minimum quality score (0–100)
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={form.min_quality_score}
              onChange={(e) =>
                setForm({ ...form, min_quality_score: Number(e.target.value) || 0 })
              }
              className="mt-1 w-24 border border-border bg-background px-3 py-2"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Articles scoring below this are held as drafts for manual review.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Tolerance floor (0–25)
            </label>
            <input
              type="number"
              min={0}
              max={25}
              value={form.tolerance_floor}
              onChange={(e) =>
                setForm({ ...form, tolerance_floor: Math.max(0, Math.min(25, Number(e.target.value) || 0)) })
              }
              className="mt-1 w-24 border border-border bg-background px-3 py-2"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              When the day is behind schedule and nothing else is ready, the publisher may promote a draft scoring
              as low as <strong>{Math.max(0, form.min_quality_score - form.tolerance_floor)}</strong> — but only once per day.
              Set to 0 to disable the fallback.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Publish times (cron just enforces the cap; times are informational)
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.publish_times.map((t, i) => (
                <div key={i} className="flex items-center gap-1">
                  <input
                    type="time"
                    value={t}
                    onChange={(e) => updateTime(i, e.target.value)}
                    className="border border-border bg-background px-2 py-1"
                  />
                  <button
                    onClick={() => removeTime(i)}
                    className="text-muted-foreground px-2"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={addTime}
                className="border border-border px-3 py-1 text-sm"
              >
                + Add
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Timezone
            </label>
            <input
              type="text"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              className="mt-1 w-full max-w-sm border border-border bg-background px-3 py-2"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="bg-foreground text-background px-5 py-2 font-medium"
            >
              {save.isPending ? "Saving…" : "Save settings"}
            </button>
            <button
              onClick={() => publishNow.mutate()}
              disabled={publishNow.isPending}
              className="border border-border px-4 py-2"
            >
              {publishNow.isPending ? "Publishing…" : "Run publisher now"}
            </button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="headline-display text-2xl">In the pipeline</h2>
        <div className="mt-4 divide-y divide-border border border-border">
          {(!ready || ready.length === 0) && (
            <div className="p-6 text-sm text-muted-foreground">
              Nothing in the pipeline yet.
            </div>
          )}
          {ready?.map((a: any) => (
            <div key={a.id} className="p-4 text-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">
                    {a.status}
                    {typeof a.quality_score === "number" && ` · score ${a.quality_score}`}
                  </div>
                  <h3 className="mt-1 font-medium truncate">{a.title}</h3>
                  {a.focus_keyword && (
                    <p className="text-xs text-muted-foreground">KW: {a.focus_keyword}</p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDate(a.updated_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
